import { useEffect, useId, useState } from 'react'
import { houseDate } from '../lib/chaseCopy'

/* House date control (E1, ux-audit 2026-09-04 §15).
 *
 * `<input type="date">` renders in the BROWSER's locale — on the audited
 * build it read mm/dd/yyyy, which is a canon breach and, worse, ambiguous:
 * an officer typing 7 August into a US-ordered box books 7 July. Every date
 * the product PRINTS is "7 Aug 2026"; this is the same rule applied to
 * entry, and the only way to be unambiguous while staying keyboard-first is
 * to take the three parts separately and name the month.
 *
 * Value in and out is ISO (yyyy-mm-dd) — the API contract is untouched. An
 * incomplete date emits '' rather than a half-parsed one. */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function split(iso: string): { d: string; m: string; y: string } {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!parts) return { d: '', m: '', y: '' }
  return { d: String(Number(parts[3])), m: parts[2], y: parts[1] }
}

function join(d: string, m: string, y: string): string {
  const day = Number(d)
  const year = Number(y)
  if (!d || !m || !y) return ''
  if (!Number.isFinite(day) || day < 1 || day > 31) return ''
  if (!Number.isFinite(year) || y.length !== 4) return ''
  return `${y}-${m}-${String(day).padStart(2, '0')}`
}

const box: React.CSSProperties = {
  font: 'inherit',
  fontSize: 'var(--text-12)',
  padding: '7px 8px',
  border: '1px solid var(--line)',
  borderRadius: 7,
  boxSizing: 'border-box',
}

export default function HouseDateInput({
  label,
  value,
  onChange,
  testId,
}: {
  /** Field name — each part's accessible name is built from it. */
  label: string
  /** ISO yyyy-mm-dd, or '' for empty. */
  value: string
  onChange: (iso: string) => void
  testId?: string
}) {
  const id = useId()
  /* The three parts live HERE, not in the parent's ISO value. An
   * incomplete date has no ISO form, so deriving the parts from `value`
   * threw away every keystroke until all three landed — the officer typed
   * a day, picked a month, and watched the day vanish. */
  const [parts, setParts] = useState(() => split(value))
  useEffect(() => {
    // A parent-driven change (form reset, a seeded value) wins; our own
    // emits round-trip to the same ISO and leave the parts alone.
    if (join(parts.d, parts.m, parts.y) !== value) setParts(split(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const { d, m, y } = parts
  const emit = (nd: string, nm: string, ny: string) => {
    setParts({ d: nd, m: nm, y: ny })
    onChange(join(nd, nm, ny))
  }
  const shown = houseDate(join(d, m, y))

  return (
    <div data-testid={testId}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          id={`${id}-d`}
          aria-label={`${label} — day`}
          inputMode="numeric"
          maxLength={2}
          placeholder="7"
          value={d}
          onChange={(e) => emit(e.target.value.replace(/\D/g, ''), m, y)}
          className="mono"
          style={{ ...box, width: 52, textAlign: 'center' }}
        />
        <select
          id={`${id}-m`}
          aria-label={`${label} — month`}
          value={m}
          onChange={(e) => emit(d, e.target.value, y)}
          style={{ ...box, flex: '0 0 96px' }}
        >
          <option value="">Month</option>
          {MONTHS.map((name, i) => (
            <option key={name} value={String(i + 1).padStart(2, '0')}>
              {name}
            </option>
          ))}
        </select>
        <input
          id={`${id}-y`}
          aria-label={`${label} — year`}
          inputMode="numeric"
          maxLength={4}
          placeholder="2026"
          value={y}
          onChange={(e) => emit(d, m, e.target.value.replace(/\D/g, ''))}
          className="mono"
          style={{ ...box, width: 72, textAlign: 'center' }}
        />
      </div>
      {shown && (
        <div
          className="mono"
          style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}
        >
          {shown}
        </div>
      )}
    </div>
  )
}
